import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { DashLine, HeaderTable, Loading, OrderEmpty, SummaryPayment } from '~/components'
import { useNavigate } from 'react-router-dom'
import { orderHistorySelector } from '~/views/OrderHistory/slices'
import { useDispatch, useSelector } from 'react-redux'
import { fetchServingOrder } from '~/apis/orderHistory'
import ServingRequestSection from '~/views/OrderHistory/components/ServingRequestSection'
import { dataCartSelector, priceTotalItemCart } from '~/redux/cartSlice'
import ButtonOrder from '~/components/ButtonOrder'
import { EXTERNAL_PARTNER, MERCHANT_TYPE, SERVING_STATUS } from '~/constants'
import { totalPriceFood } from '~/utils/convertDataToppingType'
import { useTranslation } from 'react-i18next'
import _ from 'lodash'
import ExcludesVAT from '~/views/Menu/components/ExcludesVAT'
import { ThemeContext } from '~/theme/ThemeProvider'
import { THEMES_NAME } from '~/theme/constants'

import OrderCode from '../OrderPlacedSouvenir/components/OrderCode'
import { handleScreenshotOrder } from '~/utils/media'
import PopupDeleteItem from '../Ordering/components/PopupDeleteItem'
import { useAppContext } from '~/contexts/AppContext'
import { selectRestaurant } from '~/redux/restaurantSlice'
import useCancelRequest from '~/hooks/useCancelRequest'
import { convertDataServingRequestsSmaregi } from '~/utils/orderHistory'

const OrderHistory = () => {
  const { responseServingRequest, setResponseServingRequest, saveServingSessionID } =
    useAppContext()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { data: dataOrderHistory, loading, error } = useSelector(orderHistorySelector)
  const dataCart = useSelector(dataCartSelector)
  const { tableUid, externalPartner, merchant_type } = useSelector(selectRestaurant)
  const { i18n, t } = useTranslation()
  const { currentThemeObject } = useContext(ThemeContext)
  const isTypeSouvenir = merchant_type === MERCHANT_TYPE.SOUVENIR
  const isPartnerSmaregi = externalPartner === EXTERNAL_PARTNER.Smaregi

  const [showPopupError, setShowPopupError] = useState(false)
  const [idErrorOrder, setIdErrorOrder] = useState(null)
  const { controller } = useCancelRequest()

  // biến đùng để hiển thị lỗi khi submit order
  const [messageErr, setMessageErr] = useState('')

  const {
    restaurant = { vat_rate: 0, service_rate: 0, menuExcludesVAT: null },
    service_fee,
    data,
    sub_total,
    total,
    vat_fee,
    extra_fee,
    discount_amount,
    table,
  } = dataOrderHistory

  // Xử lý lấy ra data Serving Requests dựa vào partner
  const dataServingRequests = useMemo(() => {
    if (Array.isArray(data) && data.length > 0) {
      return isPartnerSmaregi ? convertDataServingRequestsSmaregi(data) : data
    }

    return []
  }, [data, isPartnerSmaregi])

  const { serving_session_id } = table ?? {}

  // Lưu lại để lắng nghe socket
  useEffect(() => {
    if (serving_session_id) {
      saveServingSessionID(serving_session_id)
    }
  }, [serving_session_id, saveServingSessionID])

  const cart_items = useMemo(() => {
    return dataCart.map((item) => ({
      ...item,
      price: priceTotalItemCart(item),
    }))
  }, [dataCart])

  const debounceFetchData = useCallback(
    _.debounce(() => {
      dispatch(fetchServingOrder(controller))
    }, 200),
    [],
  )

  useEffect(() => {
    dispatch(fetchServingOrder(controller))
  }, [dispatch, i18n.language, tableUid, controller])

  // Lắng nghe khi thay đổi status thì call lại api
  useEffect(() => {
    if (responseServingRequest) {
      debounceFetchData()
    }

    // Khi rời khỏi màn hình này thì cần clear state đi tránh gọi api 2 lần
    return () => {
      setResponseServingRequest()
    }
  }, [responseServingRequest, debounceFetchData, setResponseServingRequest])

  const getErrorMessage = (err) => {
    setMessageErr(err)
  }

  /** render error message */
  const renderErrorMessage = () => {
    if (!messageErr) return null
    return (
      <Typography
        sx={{
          fontSize: '10px',
          color: 'red',
          marginBottom: '5px',
        }}>
        {i18n.t(messageErr)}
      </Typography>
    )
  }

  const renderHeader = () => {
    return (
      <HeaderTable
        name={table?.name || ''}
        orderCode={table?.order_code}
        onGoBack={() => navigate(-1)}
        colorText={currentThemeObject.colorText1}
      />
    )
  }

  // Chỉ hiển thị component này trong trường hợp có data và merchant type = souvenir
  const renderOrderCode = () => {
    if (isTypeSouvenir && Array.isArray(data) && data.length > 0) {
      return (
        <Box mt="24px">
          <Box color="baseColor.colorText1" fontSize="2rem" fontWeight="400" mb="24px">
            {t('titles.order_code')}
          </Box>
          <OrderCode />
          <Box fontSize="2rem" color="baseColor.colorText1" mt="24px" maxWidth="217px">
            {t('descriptions.tap_to_save_screen')}
          </Box>
          <Box
            onClick={handleScreenshotOrder}
            display="inline-block"
            my="24px"
            borderRadius="999px"
            bgcolor="baseColor.colorText1"
            color="baseColor.colorText2"
            padding="10px 22px"
            fontSize="1.2rem"
            fontWeight="600">
            {t('actions.tap_to_save_screen')}
          </Box>

          <DashLine color={currentThemeObject.colorText1} />
        </Box>
      )
    }

    return null
  }

  const renderDataCart = () => {
    return (
      Array.isArray(cart_items) &&
      cart_items.length > 0 && (
        <>
          <Box
            padding="10px"
            border={`1px solid ${currentThemeObject.color5}`}
            borderRadius="15px"
            backgroundColor="baseColor.color1">
            <ServingRequestSection
              className="cart"
              foods={cart_items}
              color={
                currentThemeObject.value === THEMES_NAME.mango
                  ? '#000000'
                  : currentThemeObject.color5
              }
              label={i18n.t('titles.in_your_cart')}
              value={`${cart_items?.length} ${i18n.t('label.items')}`}
              showDash={false}
              style={{ marginTop: 0 }}
            />
          </Box>
          <Box marginTop="1.7rem" marginBottom="1.7rem" alignItems="flex-end">
            {renderErrorMessage()}
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography
                fontSize="1.6rem"
                fontWeight="700"
                style={{ width: '100%' }}
                color="baseColor.colorText1">
                {i18n.t('titles.ready_to_order')}
              </Typography>
              <ButtonOrder
                callback={() => {
                  dispatch(fetchServingOrder())
                }}
                setMessage={(err) => getErrorMessage(err)}
                data={dataCart}
                title={i18n.t('actions.order')}
                style={{ width: '100%' }}
                setIdErrorOrder={setIdErrorOrder}
                setShowPopupError={setShowPopupError}
              />
            </Box>
          </Box>
        </>
      )
    )
  }

  // Lấy ra title của từng request
  const getTitleRequest = (item) => {
    if (isPartnerSmaregi && item.status === 'confirmed') return i18n.t('titles.order_history')

    return isTypeSouvenir
      ? i18n.t('titles.order_details')
      : i18n.t(`titles.${SERVING_STATUS[item.status].label}`)
  }

  const renderDataServingRequests = () => {
    return (
      dataServingRequests &&
      dataServingRequests.length > 0 &&
      dataServingRequests.map((e, index) => {
        const foods = e.requests.map((r) => ({
          ...r,
          price: externalPartner === 'loop' ? r.price : totalPriceFood(r),
        }))
        return (
          <ServingRequestSection
            key={index}
            className={e.status}
            foods={foods}
            color={SERVING_STATUS[e.status].color}
            dashColor={currentThemeObject.value === THEMES_NAME.default ? 'black' : 'white'}
            label={getTitleRequest(e)}
            value={`${e.requests?.length} ${i18n.t('label.items')}`}
            showDash={index !== 0}
          />
        )
      })
    )
  }

  const renderSummaryPayment = () => {
    return (
      dataServingRequests &&
      dataServingRequests.length > 0 && (
        <SummaryPayment
          total={total}
          subTotal={sub_total}
          vatMoney={vat_fee}
          vatPercent={restaurant.vat_rate}
          serviceMoney={service_fee}
          servicePercent={restaurant.service_rate}
          extraFee={extra_fee}
          discountAmount={discount_amount}
          listServingStatus={dataServingRequests}
          colorText={currentThemeObject.colorText1}
        />
      )
    )
  }

  const renderPopupDeleteItem = () => {
    if (!showPopupError) return null

    if (messageErr) setMessageErr('')

    return (
      <PopupDeleteItem
        idErrorOrder={idErrorOrder}
        showPopupError={showPopupError}
        setShowPopupError={setShowPopupError}
      />
    )
  }

  if (loading) return <Loading />
  if (!error && data && data.length === 0 && cart_items.length === 0) return <OrderEmpty />
  if (error && cart_items.length === 0) return <OrderEmpty />

  return (
    <Box
      paddingLeft="20px"
      paddingRight="20px"
      paddingBottom="20px"
      minHeight="100vh"
      bgcolor="baseColor.color1"
      id="order_code">
      {renderHeader()}
      {renderDataCart()}
      {renderOrderCode()}
      {renderDataServingRequests()}
      {renderSummaryPayment()}
      {renderPopupDeleteItem()}

      {(cart_items?.length > 0 || data?.length > 0) && <ExcludesVAT />}
    </Box>
  )
}

export default OrderHistory
